// Birdle — one bird a day, the same bird for everyone studying the same packs.
//
// Every other mode here is a ROUND: a queue off FSRS, a question, a grade, an
// interval moved. This is a PUZZLE, and the difference is the whole point of it.
// A round asks many birds one question each; a puzzle asks one bird five, and the
// five are not "which name is this" but what it eats, where it lives, how long it
// is, what its feet are for, what it builds — and then, finally, who it is.
//
// The board is five locks. Four are traits, drawn from the five axes by the day's
// own hash so the shape changes without the vocabulary changing, and one is the
// name, out of a shortlist of thirty. A TURN fills every lock still open, all at
// once, and is answered lock by lock: a lock that opens shows the bird's whole set
// and stays open, a value that was wrong is struck off that lock's list for good,
// and the photograph — which starts pixelated nearly to colour — sharpens one
// step. Six turns. Then the board opens itself and the day is lost.
//
// It grades nothing. No card, no XP, no streak, no mastery: knowing that a Marsh
// Wren nests in a dome is not the sound skill, the name skill or the photo skill,
// and charging a five-attempt puzzle to an FSRS card would teach the scheduler a
// lie about a bird the learner may know perfectly well. What it writes is the
// board being played, so a reload resumes rather than restarts, and one line per
// finished day, which is what the calendar is drawn from.
//
// A day that was missed can still be played — the calendar opens any day back
// to `FIRST_DAY` — and the record says which kind of finish it was: on the day
// itself, or afterwards.
//
// This file is the whole of the rules and none of the screen: no DOM, no media,
// no clipboard. The region is read through `getSpecies`, the way the quiz engines
// read it, so `test/birdle.test.ts` installs a fixture pack and drives all of it.

import { getSetting, setSetting } from './db';
import { dayKey } from './progress';
import { t } from '../strings';
import { mulberry32, pickDistractors } from './quiz/distractors';
import { drawMirror } from './media/mirror';
import { activePacks, getSpecies, inPackBy, packPool, speciesList } from './region';
import { hasTraits, traitsOf, TRAIT_KEYS } from './traits';
import { confusablesFor, type AudioAsset, type PhotoAsset, type Species, type SoundType, type TraitKey } from '../types';

/**
 * The pick budget — how many turns a day allows before the board opens itself.
 *
 * Six, Wordle's own number. It started at eight, on the argument that five
 * booleans a turn are far less information than Wordle's positional feedback;
 * it came down to six once it had been played, since a wrong value is struck
 * off for good and the photograph sharpens every turn. It is exactly the length
 * of the photograph's ladder (see `LADDER` in the view), so the last turn is the
 * clearest look at the bird the day ever gives.
 *
 * It is the difficulty knob, and the one number here most likely to want moving
 * again.
 */
export const TURNS = 6;

/** How many of the five axes a day asks. The fifth is the day's held card. */
export const TRAITS_ASKED = 4;

/** How many birds the name lock offers, the answer among them. */
export const NAME_CHOICES = 30;

/**
 * How many of those are held back for birds from outside the answer's
 * neighbourhood — half of them.
 *
 * All thirty used to come off the distractor cascade, and thirty is far deeper
 * than a cascade is meant to go: a sparrow's list was every sparrow, finch and
 * junco in the pack, so the name lock could only be opened from the photograph,
 * and the four trait columns — which exist to narrow the list — narrowed nothing,
 * because every bird on it ate, lived and nested alike. Half near keeps the
 * answer among the birds it is honestly confused with; half far gives what
 * the board has already learnt something to rule out.
 */
export const FAR_NAMES = NAME_CHOICES / 2;

/**
 * The most birds any one far family puts on the list.
 *
 * Fifteen slots and a cap of five is three families at least, so the far half
 * is a few groups rather than one: Anatidae alone is thirty-two PNW birds and
 * would otherwise take the whole half whenever it came up.
 */
const FAR_PER_FAMILY = 5;

/**
 * Families at least this big are drawn before smaller ones.
 *
 * Twenty-odd PNW families are a single species, and a far half made of them
 * would be fifteen strangers rather than a few families — the shape a list
 * takes when eliminating by "not a duck" is the point.
 */
const FAR_FAMILY_MIN = 3;

/** One lock on the board: a trait axis, or the bird's name. */
export type Slot = TraitKey | 'name';

/** The day's board, in the order it is drawn. */
export interface BirdlePuzzle {
  /** `dayKey(now)` — the local calendar day this puzzle belongs to. */
  day: string;
  /** The hidden bird. */
  code: string;
  /** The four axes this day asks, in `TRAIT_KEYS` order. */
  asked: TraitKey[];
  /** The name lock's shortlist, shuffled, containing `code`. */
  names: string[];
  /** Which voice the strip plays. */
  voice: SoundType;
  /**
   * Which recording of that voice, by asset id.
   *
   * Fixed for the day rather than drawn per turn: the strip is a clue, and a
   * clue that quietly became a different recording every time the board
   * re-rendered would be a different puzzle each turn for anyone listening
   * closely — and the strip draws the clip's own shape, so the change would be
   * visible as well as audible.
   *
   * Read it with `dailyClip`, never by id alone: an id is not unique within a
   * species. Xeno-canto files some recordings under two sound types at once, and
   * the pack carries them once per type — Cackling Goose and California Gull each
   * ship one recording that is both a call and a song.
   */
  clip: string;
  /**
   * Which photograph the board pixelates, by asset id — drawn per day, like the
   * clip, so a bird with two pictures isn't always shown by the same one, and
   * fixed within the day so the picture doesn't change between turns.
   */
  photo: string;
  /**
   * Whether that photograph goes up mirrored — see core/media/mirror.ts. Part
   * of the day like the photo itself, so the pixelated clue and the picture on
   * the done panel face the same way, and every player's board matches.
   */
  mirrored: boolean;
}

/** One submitted turn: a value per lock that was open when it was submitted. */
export type BirdleRow = Partial<Record<Slot, string>>;

/**
 * A 32-bit hash of a string, for seeding `mulberry32`.
 *
 * FNV-1a: three lines, no dependency, and stable across engines and versions,
 * which matters more here than in any other use of a hash in this app — two
 * learners on two devices have to arrive at the same number from the same day.
 */
function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * A stable name for a pack selection, for the seed and for the saved board.
 *
 * Ids and NOT versions. It used to carry both, on the argument that a pack that
 * has grown is not the same pack — which made every pack update re-deal every
 * date at once. That cost nothing while the only day anyone could play was
 * today; with a calendar it would have meant the bird for the third of October
 * depended on which week you opened it. What a growing pack may and may not
 * change is now settled per day instead, in `dailyPuzzle`.
 *
 * A different SELECTION is still a different puzzle: the Pacific Northwest's
 * bird of the day is not Cape Town's, and neither is "All packs"'s.
 */
export function poolKey(packs: { id: string }[]): string {
  return packs
    .map((p) => p.id)
    .sort()
    .join('+');
}

/**
 * Whether a bird can be the day's bird.
 *
 * All five axes (four are asked and the fifth still has to be a real fact for the
 * done panel to print), a photograph for the ladder, and a recording for the
 * speaker. The audio check is not theoretical: two species ship deliberately mute,
 * because every recording Xeno-canto holds of them is CC-BY-ND — without it the
 * puzzle's only sound clue would be a dead button once every few months.
 */
export function eligible(sp: Species): boolean {
  return hasTraits(sp) && sp.photos.length > 0 && sp.audio.length > 0;
}

/**
 * Pick one of `pool` for `seed`, stably under growth.
 *
 * NOT `pool[hash % pool.length]`. Adding twenty birds to a pack re-maps every
 * index, so the modulo form would change the answer for every past and future day
 * at once; scoring each species on a hash of the day AND its own code, and taking
 * the winner, means a new bird can only ever change the days it actually wins.
 */
function pickStable(pool: Species[], seed: string): Species | undefined {
  let best: Species | undefined;
  let bestScore = -1;
  for (const sp of pool) {
    const score = hash(`${seed}/${sp.code}`);
    if (score > bestScore) {
      bestScore = score;
      best = sp;
    }
  }
  return best;
}

/** `n` of `list`, shuffled, deterministic under `rnd`. */
function shuffle<T>(list: T[], rnd: () => number): T[] {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * The name lock's shortlist, less the answer, in its two halves.
 *
 * NEAR is the ordinary distractor cascade — the confusable cluster, then the
 * family, then the taxonomic neighbourhood — which is the list of birds this one
 * is genuinely mistaken for. FAR is `FAR_NAMES` birds from a few families that
 * share nothing with the near half: not the answer's family, not any family the
 * cascade reached, and nothing in the answer's confusable clusters under any
 * skill, so a far bird is never a sound-alike from next door by another route.
 *
 * `codes` is the pool to draw from, answer included or not. A pool too small to
 * fill the far half gives the room back to the near one, so the list is still
 * `NAME_CHOICES` long wherever the pack has that many birds.
 */
export function nameShortlist(
  code: string,
  codes: string[],
  rnd: () => number,
): { near: string[]; far: string[] } {
  const bird = getSpecies(code);
  const others = NAME_CHOICES - 1;
  const near = pickDistractors(code, codes, others - FAR_NAMES, 'sound', rnd);

  const related = new Set<string>([code, ...near]);
  for (const skill of ['sound', 'names', 'photo'] as const) {
    for (const c of confusablesFor(bird, skill)) related.add(c);
  }
  const nearFamilies = new Set([bird?.family, ...near.map((c) => getSpecies(c)?.family)]);

  const byFamily = new Map<string, string[]>();
  for (const c of codes) {
    const family = getSpecies(c)?.family;
    if (!family || related.has(c) || nearFamilies.has(family)) continue;
    byFamily.set(family, [...(byFamily.get(family) ?? []), c]);
  }
  // Shuffled, then the big families stably sorted ahead of the small ones, so
  // which families come up varies by day and the draw still prefers a group.
  const families = shuffle([...byFamily.values()], rnd).sort(
    (a, b) => Number(b.length >= FAR_FAMILY_MIN) - Number(a.length >= FAR_FAMILY_MIN),
  );
  const far: string[] = [];
  for (const members of families) {
    const room = Math.min(FAR_PER_FAMILY, FAR_NAMES - far.length);
    if (room <= 0) break;
    far.push(...shuffle(members, rnd).slice(0, room));
  }

  const short = others - near.length - far.length;
  if (short > 0) {
    const taken = new Set([...near, ...far]);
    near.push(...pickDistractors(code, codes.filter((c) => !taken.has(c)), short, 'sound', rnd));
  }
  return { near, far };
}

/**
 * Build the day's puzzle, or null when the selection has nothing to ask about.
 *
 * `pool` must be the region's OWN species list — `speciesList()`, never
 * `ignored.ts`'s `studyList()`. Every quiz pool in this app comes from the
 * ignored list on purpose, and this one is the deliberate exception: setting a
 * bird aside is a private decision about one's own revision, and if it moved the
 * daily the puzzle would quietly stop being the same puzzle for everyone, which is
 * the only thing it has that the other modes don't.
 *
 * A day's puzzle is meant to stay that day's puzzle — the calendar lets it be
 * played a month on, and the record of a day already played is checked against
 * it. Three things hold it still while the pack around it changes:
 *
 * - The seed is the day and the selection, with no pack version in it.
 * - Only birds that had joined the pack BY that day are in the draw
 *   (`inPackBy`), so a batch added later cannot win a day that came before it.
 *   Together with `pickStable` that makes the bird a fact about the date.
 * - Each part of the board draws from its own stream. They used to share one,
 *   and the shortlist takes a different number of draws from it depending on
 *   how many birds there are to choose among — so a bigger pack moved the
 *   voice, the clip and the photograph of every day, bird unchanged.
 *
 * What still moves a past day: a bird being withdrawn or losing what makes it
 * `eligible`, and the gap between a batch being stamped by the pipeline and
 * being shipped, during which the app is still drawing without it.
 */
export function dailyPuzzle(
  day: string,
  pool: string,
  species: Species[],
): BirdlePuzzle | null {
  const askable = species.filter((sp) => eligible(sp) && inPackBy(sp, day));
  if (!askable.length) return null;

  const seed = `${day}|${pool}`;
  const bird = pickStable(askable, seed);
  if (!bird) return null;

  /** One part of the board's own run of numbers — see above. */
  const stream = (part: string): (() => number) => mulberry32(hash(`${seed}#${part}`));

  // Four of the five axes. Shuffled then re-sorted into TRAIT_KEYS order, so
  // which four varies by day and their order on the board never does — the board
  // is a thing you learn to read, and a board that reshuffles its columns is a
  // new board every morning.
  const asked = shuffle([...TRAIT_KEYS], stream('axes')).slice(0, TRAITS_ASKED);
  asked.sort((a, b) => TRAIT_KEYS.indexOf(a) - TRAIT_KEYS.indexOf(b));

  // Inside the answer's own pack: a Cape Sugarbird among Pacific Northwest
  // sparrows is a free mark for the same reason it is in every quiz.
  const codes = packPool(bird.code, askable.map((sp) => sp.code));
  const pick = stream('names');
  const { near, far } = nameShortlist(bird.code, codes, pick);
  const names = shuffle([bird.code, ...near, ...far], pick);

  // One voice for the day, chosen from what the bird actually has rather than
  // from the three types — a song asked for and not held would be a silent clue
  // — and then one recording of it.
  const heard = stream('voice');
  const voices = [...new Set(bird.audio.map((a) => a.type))];
  const voice = voices[Math.floor(heard() * voices.length)];
  const takes = bird.audio.filter((a) => a.type === voice);
  const clip = takes[Math.floor(heard() * takes.length)].id;

  const photo = bird.photos[Math.floor(stream('photo')() * bird.photos.length)].id;
  const mirrored = drawMirror(stream('mirror'));

  return { day, code: bird.code, asked, names, voice, clip, photo, mirrored };
}

/** The photograph the day's board shows; the first one if the id has gone. */
export function dailyPhoto(bird: Species, puzzle: BirdlePuzzle): PhotoAsset | undefined {
  return bird.photos.find((p) => p.id === puzzle.photo) ?? bird.photos[0];
}

/**
 * The recording the day's strip plays.
 *
 * Matched on the voice as well as the id, for the reason on `clip` above. Falls
 * back to the bird's first recording, which cannot happen for a puzzle built
 * against the same manifest it is being read against, and is the right answer if
 * one ever is — a strip with no asset is a screen with a hole in it.
 */
export function dailyClip(bird: Species, puzzle: BirdlePuzzle): AudioAsset {
  return (
    bird.audio.find((a) => a.id === puzzle.clip && a.type === puzzle.voice) ?? bird.audio[0]
  );
}

/** The board's locks, in drawing order: the day's four axes, then the name. */
export function slots(puzzle: BirdlePuzzle): Slot[] {
  return [...puzzle.asked, 'name'];
}

/**
 * Whether a value opens a lock.
 *
 * A trait lock is opened by ANY ONE value the bird holds. A robin that eats
 * insects, fruit and worms is solved by naming any of the three, which makes the
 * generalist the easy puzzle and the specialist the hard one — the right way
 * round, and the reason every axis is a list.
 */
export function opens(bird: Species, slot: Slot, value: string): boolean {
  if (slot === 'name') return value === bird.code;
  return traitsOf(bird)[slot].includes(value);
}

/** Which locks are open after the rows played so far. */
export function solved(puzzle: BirdlePuzzle, bird: Species, rows: BirdleRow[]): Set<Slot> {
  const open = new Set<Slot>();
  for (const row of rows) {
    for (const slot of slots(puzzle)) {
      const value = row[slot];
      if (value !== undefined && opens(bird, slot, value)) open.add(slot);
    }
  }
  return open;
}

/**
 * The turn a lock opened on, 1-based, or 0 for one still shut.
 *
 * The share grid is drawn from this rather than from `solved`: a staircase is
 * WHICH turn each lock fell on, and a set of open locks has thrown that away.
 */
export function openedOn(
  puzzle: BirdlePuzzle,
  bird: Species,
  rows: BirdleRow[],
): Record<Slot, number> {
  const turn = {} as Record<Slot, number>;
  for (const slot of slots(puzzle)) turn[slot] = 0;
  rows.forEach((row, i) => {
    for (const slot of slots(puzzle)) {
      if (turn[slot]) continue;
      const value = row[slot];
      if (value !== undefined && opens(bird, slot, value)) turn[slot] = i + 1;
    }
  });
  return turn;
}

/**
 * Values already tried and wrong, per lock.
 *
 * A wrong pick costs the turn and buys an elimination, which is the only thing a
 * miss here is worth — so it has to be visible and it has to stick. Values from
 * a lock that has since opened are dropped: the lock is finished, and a struck-out
 * chip under an answered question is noise.
 */
export function ruledOut(
  puzzle: BirdlePuzzle,
  bird: Species,
  rows: BirdleRow[],
): Record<Slot, Set<string>> {
  const open = solved(puzzle, bird, rows);
  const out = {} as Record<Slot, Set<string>>;
  for (const slot of slots(puzzle)) out[slot] = new Set<string>();
  for (const row of rows) {
    for (const slot of slots(puzzle)) {
      if (open.has(slot)) continue;
      const value = row[slot];
      if (value !== undefined && !opens(bird, slot, value)) out[slot].add(value);
    }
  }
  return out;
}

export interface BirdleOutcome {
  /** Turns played. */
  turns: number;
  /** Turns left in the budget. */
  left: number;
  /** The board is finished, won or not. */
  over: boolean;
  /** Every lock open. */
  won: boolean;
}

export function outcome(
  puzzle: BirdlePuzzle,
  bird: Species,
  rows: BirdleRow[],
): BirdleOutcome {
  const open = solved(puzzle, bird, rows);
  const won = slots(puzzle).every((s) => open.has(s));
  const turns = rows.length;
  return { turns, left: Math.max(0, TURNS - turns), over: won || turns >= TURNS, won };
}

/**
 * Whether a row can be submitted: every lock still shut has a value, and none of
 * them is a value already ruled out.
 *
 * Completeness is required rather than allowed. A miss costs only the turn and
 * pays back an elimination, so there is no version of this game where holding a
 * lock back helps — and a row that could be part-filled would make the budget
 * mean different things on different turns.
 */
export function rowReady(
  puzzle: BirdlePuzzle,
  bird: Species,
  rows: BirdleRow[],
  row: BirdleRow,
): boolean {
  const open = solved(puzzle, bird, rows);
  const struck = ruledOut(puzzle, bird, rows);
  return slots(puzzle).every((slot) => {
    if (open.has(slot)) return true;
    const value = row[slot];
    return value !== undefined && !struck[slot].has(value);
  });
}

const CELL_NEW = '🟩';
const CELL_OPEN = '⬜';
const CELL_SHUT = '⬛';

/**
 * The result as a spoiler-free grid, for the clipboard.
 *
 * One line per turn, one cell per lock, in board order: green where that lock
 * opened on that turn, white where it was already open, black where it was still
 * shut. That is the staircase, and it says how the solve went without naming a
 * bird, an axis or a value — the whole point of a daily being comparable is that
 * the comparison can happen before the other person has played.
 */
export function shareText(
  puzzle: BirdlePuzzle,
  bird: Species,
  rows: BirdleRow[],
  opts: { date: string },
): string {
  const { turns, won } = outcome(puzzle, bird, rows);
  const turn = openedOn(puzzle, bird, rows);
  const board = slots(puzzle);
  const grid = rows.map((_, i) =>
    board
      .map((slot) => {
        const at = turn[slot];
        if (at === i + 1) return CELL_NEW;
        return at && at <= i ? CELL_OPEN : CELL_SHUT;
      })
      .join(''),
  );
  const score = won
    ? t('birdle.share.won', { turns, max: TURNS })
    : t('birdle.share.lost', { max: TURNS });
  return [t('birdle.share.title', { date: opts.date }), score, ...grid].join('\n');
}

// ---- Today ----------------------------------------------------------------
//
// What the mode persists: which day the board on screen belongs to, which pack
// selection it was built for, and the rows played — plus, below, one line per
// finished day. No score is kept, because nothing is scored — a solved day is a
// solved day and tomorrow is a new bird.

const K_TODAY = 'birdle.today';

export interface BirdleSaved {
  day: string;
  pool: string;
  rows: BirdleRow[];
}

/** Today's day key and pack selection, which together name the day's puzzle. */
export function today(now: Date = new Date()): { day: string; pool: string } {
  return {
    // `dayKey` is the local calendar day, the same one the streak strip counts
    // in, so "today's bird" and "today" are the same day on one device.
    day: dayKey(now),
    pool: poolKey(activePacks()),
  };
}

/**
 * The rows already played today, or none.
 *
 * A saved board from another day — or from a different pack selection, which is a
 * different puzzle — is not resumed and not migrated. There is no `deleteSetting`,
 * so the record simply gets overwritten by the first turn of the new day; the same
 * self-expiry the daily-done marker uses.
 */
export async function loadToday(day: string, pool: string): Promise<BirdleRow[]> {
  const saved = await getSetting<BirdleSaved>(K_TODAY);
  if (!saved || saved.day !== day || saved.pool !== pool) return [];
  return Array.isArray(saved.rows) ? saved.rows : [];
}

/**
 * Write the board back, once per turn.
 *
 * `setSetting` serialises the WHOLE settings map on every call, which on the
 * deployed backend is one full-record PATCH — so this is called when a turn is
 * played and never when a chip is tapped.
 */
export async function saveToday(day: string, pool: string, rows: BirdleRow[]): Promise<void> {
  await setSetting(K_TODAY, { day, pool, rows } satisfies BirdleSaved);
}

// ---- Other days -----------------------------------------------------------
//
// The calendar opens any day back to the first one, so a board can belong to a
// day that is not today. Those are kept apart from `birdle.today`, which stays
// exactly what it was — the Home card reads it on every visit — and there are a
// few of them rather than one, because a single slot would throw away a board
// left half-played the moment another day was opened.

const K_PAST = 'birdle.past';

/** How many other days' boards are kept, most recently played first. */
export const PAST_BOARDS = 6;

/** The first day there is a Birdle for: the day the mode shipped. */
export const FIRST_DAY = '2026-09-12';

/**
 * A day key the calendar may open, or null.
 *
 * It arrives from the address bar, so it is checked rather than trusted: a real
 * calendar day, no earlier than the first Birdle and no later than today.
 * Tomorrow's bird is as computable as yesterday's, which is the reason to refuse
 * it here rather than leave it to whoever builds the link.
 */
export function playableDay(raw: string | undefined, now: Date = new Date()): string | null {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split('-').map(Number);
  // Round-tripped, so the 31st of a thirty-day month is not a day.
  if (dayKey(new Date(y, m - 1, d)) !== raw) return null;
  return raw >= FIRST_DAY && raw <= dayKey(now) ? raw : null;
}

async function loadPast(): Promise<BirdleSaved[]> {
  const saved = await getSetting<BirdleSaved[]>(K_PAST);
  return Array.isArray(saved) ? saved : [];
}

/**
 * The rows already played on `day`, today or not.
 *
 * The other-days list first, then today's slot. That order covers both ways a
 * board ends up on the far side of midnight: one finished after it had become
 * yesterday's is in the list, and one finished on its own day is still in
 * today's slot until the next day's first turn overwrites it.
 */
export async function loadBoard(day: string, pool: string): Promise<BirdleRow[]> {
  const kept = (await loadPast()).find((b) => b.day === day && b.pool === pool);
  if (kept && Array.isArray(kept.rows)) return kept.rows;
  return loadToday(day, pool);
}

/** Write a board back, once per turn — to today's slot or to the other-days list. */
export async function saveBoard(
  day: string,
  pool: string,
  rows: BirdleRow[],
  now: Date = new Date(),
): Promise<void> {
  if (day === dayKey(now)) return saveToday(day, pool, rows);
  const past = await loadPast();
  const next = [{ day, pool, rows }, ...past.filter((b) => b.day !== day)].slice(0, PAST_BOARDS);
  await setSetting(K_PAST, next);
}

/**
 * The days this pack selection has a board with turns on it, finished or not.
 *
 * The calendar marks the ones that are not on record as started, so a day left
 * half-played does not look the same as one never opened.
 */
export async function startedDays(pool: string): Promise<Set<string>> {
  const boards = [...(await loadPast()), await getSetting<BirdleSaved>(K_TODAY)];
  const days = new Set<string>();
  for (const b of boards) {
    if (b && b.pool === pool && Array.isArray(b.rows) && b.rows.length) days.add(b.day);
  }
  return days;
}

// ---- Past days ------------------------------------------------------------
//
// A finished day, kept as the bird and how it went and nothing else — not the
// rows, which would be the whole board a year over in the settings map that is
// PATCHed whole on every write. Enough to say "Tuesday was the Varied Thrush, in
// four", which is what the calendar draws, one cell a day.

const K_HISTORY = 'birdle.history';

/**
 * How many finished days are kept, newest first.
 *
 * A year, which is as far back as a calendar of a daily gets paged. It was
 * thirty while the only reader was a list of seven; the difference costs about
 * sixty bytes a day in the settings record, so a full year is some 20 KB riding
 * along on every settings write.
 */
export const HISTORY_DAYS = 366;

export interface BirdleDay {
  day: string;
  code: string;
  won: boolean;
  turns: number;
  /**
   * Finished on a later day than its own — played from the calendar.
   *
   * Absent rather than false for a day finished on the day, which is also what
   * every record written before the calendar existed looks like, and rightly:
   * there was no way to play a day late then.
   */
  late?: true;
}

/** The finished days on record, newest first. */
export async function loadHistory(): Promise<BirdleDay[]> {
  const saved = await getSetting<BirdleDay[]>(K_HISTORY);
  return Array.isArray(saved) ? saved : [];
}

/**
 * Put a finished day on record, replacing any earlier entry for the same day.
 *
 * Called every time the done panel draws, so it has to be free when there is
 * nothing new: an entry already on record exactly as given writes nothing. A day
 * finished twice — the pack selection changed in between, which is a second
 * puzzle — keeps the later one; the list is one line per day.
 */
export async function recordDay(
  entry: Omit<BirdleDay, 'late'>,
  now: Date = new Date(),
): Promise<void> {
  const past = await loadHistory();
  const was = past.find((d) => d.day === entry.day);
  if (
    was &&
    was.code === entry.code &&
    was.won === entry.won &&
    was.turns === entry.turns
  ) {
    // Which is also what keeps `late` honest: a day finished on the day and
    // looked at again a week later has not been finished again.
    return;
  }
  const fresh: BirdleDay = entry.day === dayKey(now) ? { ...entry } : { ...entry, late: true };
  const next = [fresh, ...past.filter((d) => d.day !== entry.day)]
    .sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0))
    .slice(0, HISTORY_DAYS);
  await setSetting(K_HISTORY, next);
}

/**
 * A finished day's board, rebuilt from its record alone.
 *
 * For a day whose puzzle can no longer be drawn the way it was: the pack has a
 * new version since, or the selection has changed, and `dailyPuzzle` would now
 * hand that date a different bird. The record still knows which bird it was, so
 * the done panel can show it — with its first photograph and first recording,
 * since which ones the day drew went with the draw. Null when the bird is not in
 * the packs selected now.
 */
export function recordedPuzzle(entry: BirdleDay): BirdlePuzzle | null {
  const bird = getSpecies(entry.code);
  if (!bird || !bird.audio.length) return null;
  return {
    day: entry.day,
    code: bird.code,
    asked: [],
    names: [bird.code],
    voice: bird.audio[0].type,
    clip: bird.audio[0].id,
    photo: bird.photos[0]?.id ?? '',
    mirrored: false,
  };
}

/** The record added up, for the foot of the calendar. */
export interface BirdleTally {
  played: number;
  won: number;
  /** Finished on the day itself, won or not. */
  onDay: number;
  /** Days won in one turn, in two, and so on — `TURNS` entries. */
  byTurns: number[];
  lost: number;
}

/**
 * How the finished days went.
 *
 * Days counted by how many turns they took, and nothing that can be lost: no
 * streak, because a streak is a number whose only news is the day it resets, and
 * a daily that can be played late has no honest way to keep one anyway.
 */
export function tally(days: BirdleDay[]): BirdleTally {
  const out: BirdleTally = {
    played: days.length,
    won: 0,
    onDay: 0,
    byTurns: Array.from({ length: TURNS }, () => 0),
    lost: 0,
  };
  for (const d of days) {
    if (!d.late) out.onDay++;
    if (!d.won) {
      out.lost++;
      continue;
    }
    out.won++;
    out.byTurns[Math.min(TURNS, Math.max(1, d.turns)) - 1]++;
  }
  return out;
}

/**
 * A month as the calendar draws it: whole weeks, Monday first, a day key per
 * cell and null for the cells either side of the month.
 *
 * Monday, because that is where this app's week starts everywhere else — the
 * rest-day allowance is counted Monday to Sunday (`weekKey` in progress.ts).
 */
export function monthCells(month: string): (string | null)[] {
  const [y, m] = month.split('-').map(Number);
  const lead = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  const length = new Date(y, m, 0).getDate();
  const cells: (string | null)[] = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= length; d++) cells.push(dayKey(new Date(y, m - 1, d)));
  while (cells.length % 7) cells.push(null);
  return cells;
}

/** The month `by` months from `month`, both as `YYYY-MM`. */
export function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number);
  return dayKey(new Date(y, m - 1 + by, 1)).slice(0, 7);
}

/** What the Home card says about the day: whether there is one, and how it went. */
export interface BirdleStanding {
  /** The selection has a bird that can be asked about. */
  ready: boolean;
  turns: number;
  over: boolean;
  won: boolean;
}

/**
 * Today at a glance, for the Home card.
 *
 * Everything here is already in memory — the manifest, and the settings map that
 * is hydrated at boot — so this costs a hash over the pool and no I/O at all.
 */
export async function birdleStanding(now: Date = new Date()): Promise<BirdleStanding> {
  const { day, pool } = today(now);
  const puzzle = dailyPuzzle(day, pool, speciesList());
  const bird = puzzle ? getSpecies(puzzle.code) : undefined;
  if (!puzzle || !bird) return { ready: false, turns: 0, over: false, won: false };
  const rows = await loadToday(day, pool);
  return { ready: true, ...outcome(puzzle, bird, rows) };
}
